"use client";

import dynamic from "next/dynamic";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";

import { Button } from "@tripcart/traveler-ui/components/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@tripcart/traveler-ui/components/sheet";
import { cx } from "@tripcart/traveler-ui/cx";
import type { DepartureDto } from "@tripcart/shared";

import { LazyPopover } from "@/components/lazy-floating/popover.client";

import { useIsDesktop } from "../_use-is-desktop.client";
import type { Translator } from "@/lib/translations";

import { CalendarSkeleton } from "./_calendar-skeleton";
import {
  CALENDAR_SLOT_CLASSNAMES,
  CALENDAR_SURFACE_CLASS,
} from "../_calendar-theme";
import type { CalendarDayPrice } from "../_calendar-pricing";
import styles from "./_date-field.module.css";

const AvailabilityCalendar = dynamic(
  () =>
    import("../_availability-calendar").then((m) => ({
      default: m.AvailabilityCalendar,
    })),
  {
    ssr: false,
    loading: () => <CalendarSkeleton />,
  },
);

/** Force-fetch the calendar chunk (react-day-picker + date-fns ≈ 50 KB
 *  gzipped) so by the time the user opens the picker it's already in the
 *  browser cache and the dynamic import resolves synchronously.
 *  Webpack/Next dedupes repeated `import()` calls, so this is cheap to call
 *  from multiple intent signals (idle, hover, focus). */
export function prefetchAvailabilityCalendar(): void {
  void import("../_availability-calendar");
}

export interface DateFieldProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDate: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  departures: DepartureDto[] | null;
  prices: Map<string, CalendarDayPrice> | undefined;
  totalTravelers: number;
  numberOfMonths: number;
  onMonthChange: (month: Date) => void;
  /** True when a price on the grid lost precision to rounding — see
   *  `hasRoundedPrice` at the call site. The exact figure stays available in
   *  each day's accessible label and in the widget total. */
  hasRoundedPrice: boolean;
  showError: boolean;
  formatDate: (
    date: Date,
    options?: { weekday?: "short"; monthStyle?: "short" },
  ) => string;
  t: Translator;
}

export function DateField(props: DateFieldProps) {
  const isDesktop = useIsDesktop();
  // PULLED OUT OF `props`, and that is not a style preference. `pnpm i18n`
  // scrapes calls on the bare identifier `t`, so `props.t("…")` is invisible to
  // it: the string never reaches the catalog, no operator can translate it, and
  // `pnpm i18n --check` still reports the catalog up to date. Every other
  // literal on this page goes through a bare `t`.
  const { t } = props;
  const trigger = <DateTrigger {...props} />;
  const body = <CalendarBody {...props} />;

  if (!isDesktop) {
    return (
      <>
        {trigger}
        <Sheet open={props.open} onOpenChange={props.onOpenChange}>
          <SheetContent side="bottom">
            <SheetHeader>
              <SheetTitle>{t("Select a departure")}</SheetTitle>
            </SheetHeader>
            <SheetBody>{body}</SheetBody>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <LazyPopover
      open={props.open}
      onOpenChange={props.onOpenChange}
      side="left"
      align="start"
      sideOffset={12}
      // `max-w` caps the popover to the viewport so it never spills off-screen:
      // the availability legend below is a ~375px one-liner that otherwise
      // sizes the popover wider than the calendar.
      contentClassName={styles.popover}
      renderTrigger={(triggerProps) => (
        <DateTrigger {...props} triggerProps={triggerProps} />
      )}
    >
      {body}
    </LazyPopover>
  );
}

/** The field itself. Identical in both branches — only what it opens differs —
 *  so it is one component and the two placements cannot drift apart. */
function DateTrigger({
  selectedDate,
  showError,
  formatDate,
  onOpenChange,
  t,
  triggerProps,
}: DateFieldProps & { triggerProps?: Record<string, unknown> }) {
  return (
    <Button
      variant="field"
      size="lg"
      // Guide `.bk-date.is-set` — once a departure is chosen the field takes
      // the brand border and the heading's weight. It is the one answer the
      // card cannot compute for the traveller, so a set date reads as settled
      // rather than as one more grey box.
      fieldState={selectedDate ? "set" : "placeholder"}
      aria-invalid={showError || undefined}
      {...triggerProps}
      // The popover branch supplies its own `onClick` through `triggerProps`;
      // the sheet branch has no anchor to attach to, so it opens here.
      {...(triggerProps ? {} : { onClick: () => onOpenChange(true) })}
      onMouseEnter={prefetchAvailabilityCalendar}
      onFocus={(e) => {
        prefetchAvailabilityCalendar();
        (
          triggerProps?.onFocus as ((ev: React.FocusEvent) => void) | undefined
        )?.(e);
      }}
    >
      <div className={styles.value}>
        <CalendarIcon
          className={cx(styles.icon, showError && styles.iconInvalid)}
        />
        <span className={styles.label}>
          {selectedDate
            ? // WORDS, and not the agency's numeric `dateFormat` — the same
              // choice the departures list makes. This field and that list
              // name the SAME departure, so a numeric date here beside
              // "Sun, Oct 11, 2026" there reads as two different days.
              formatDate(selectedDate, {
                weekday: "short",
                monthStyle: "short",
              })
            : // Guide `.bk-date` — "Choose a departure", not "Choose a Date".
              // What the reader picks is a scheduled departure, and the list
              // they land in is departures; naming the date names the input
              // rather than the thing being chosen.
              t("Choose a departure")}
        </span>
      </div>
      <ChevronDown className={styles.icon} />
    </Button>
  );
}

/** The calendar and its legend — the same tree in the sheet and the popover. */
function CalendarBody({
  selectedDate,
  onSelect,
  onOpenChange,
  departures,
  prices,
  totalTravelers,
  numberOfMonths,
  onMonthChange,
  hasRoundedPrice,
  t,
}: DateFieldProps) {
  return (
    <>
      <AvailabilityCalendar
        selected={selectedDate}
        onSelect={(date) => {
          onSelect(date);
          onOpenChange(false);
        }}
        departures={departures}
        prices={prices}
        totalTravelers={totalTravelers}
        numberOfMonths={numberOfMonths}
        onMonthChange={onMonthChange}
        className={CALENDAR_SURFACE_CLASS}
        classNames={CALENDAR_SLOT_CLASSNAMES}
      />
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={cx(styles.legendDot, styles.seasonal)} />
          <span className={styles.legendLabel}>{t("Seasonal Price")}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={cx(styles.legendDot, styles.groupDiscount)} />
          <span className={styles.legendLabel}>
            {t("Group Discount Available")}
          </span>
        </div>
        <div className={styles.legendItem}>
          <span className={cx(styles.legendDot, styles.lowStock)} />
          <span className={styles.legendLabel}>{t("Few Spots Left")}</span>
        </div>
      </div>
      {hasRoundedPrice ? (
        <p className={styles.roundingNote}>{t("Prices may be rounded")}</p>
      ) : null}
    </>
  );
}
